/**
 * launch-animation.tsx
 *
 * AAA branded launch intro — plays on every native cold start (and is mounted
 * above the router in App.tsx, wrapped in <AnimatePresence>).
 *
 * The native splash is just a SOLID dark color (no logo), so this web animation
 * IS the brand reveal: an expanding glow halo + light sweep + ring pulse, the
 * logo springs in (de-blur + glow), the wordmark rises, then it fades out into
 * the app. Fast, smooth, seamless — no static logo.
 */

import { useEffect } from 'react';
import { motion } from 'motion/react';

interface LaunchAnimationProps {
  onDone: () => void;
}

// Moonlight accent (matches the app theme)
const ACCENT = 'rgba(99,119,194,';

export function LaunchAnimation({ onDone }: LaunchAnimationProps) {
  // Quick intro — auto-dismiss after ~1800 ms; tap skips immediately.
  useEffect(() => {
    const t = window.setTimeout(onDone, 1800);
    return () => window.clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      key="launch-animation"
      className="fixed inset-0 z-[300] grid place-items-center overflow-hidden"
      style={{
        background: 'radial-gradient(circle at 50% 42%, #131A34 0%, #0C1020 64%)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        cursor: 'pointer',
      }}
      // Starts already visible (matches the solid dark native splash → no flash)
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, filter: 'blur(6px)', transition: { duration: 0.5, ease: 'easeInOut' } }}
      onClick={onDone}
    >
      {/* Expanding glow halo */}
      <motion.div
        className="absolute h-72 w-72 rounded-full"
        style={{ background: `radial-gradient(circle, ${ACCENT}0.30), transparent 70%)` }}
        initial={{ scale: 0.2, opacity: 0 }}
        animate={{ scale: [0.2, 1.7, 1.25], opacity: [0, 0.95, 0.45] }}
        transition={{ duration: 1.6, ease: 'easeOut' }}
      />

      {/* Horizontal light sweep */}
      <motion.div
        className="absolute h-px w-80"
        style={{ background: `linear-gradient(to right, transparent, ${ACCENT}0.85), transparent)` }}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: [0, 1, 0], scaleX: [0, 1, 1] }}
        transition={{ duration: 1.0, delay: 0.4, ease: 'easeOut' }}
      />

      {/* Concentric ring pulse */}
      <motion.div
        className="absolute rounded-full"
        style={{ border: `1px solid ${ACCENT}0.35)` }}
        initial={{ width: 80, height: 80, opacity: 0 }}
        animate={{ width: 280, height: 280, opacity: [0, 0.6, 0] }}
        transition={{ duration: 1.5, delay: 0.25, ease: 'easeOut' }}
      />

      {/* Brand content */}
      <div className="relative flex flex-col items-center gap-4 select-none">
        <motion.img
          src="/log-white.png"
          alt=""
          className="h-24 w-24 object-contain"
          initial={{ scale: 0.6, opacity: 0, filter: 'blur(10px) drop-shadow(0 0 0 rgba(99,119,194,0))' }}
          animate={{ scale: 1, opacity: 1, filter: 'blur(0px) drop-shadow(0 0 26px rgba(99,119,194,0.5))' }}
          transition={{ type: 'spring', stiffness: 210, damping: 17, delay: 0.12 }}
        />

        <motion.div
          className="font-serif font-bold tracking-tight"
          style={{ fontSize: 34, color: '#DEE5F4' }}
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          reformu<span style={{ color: '#9DB0E6' }}>.</span>lat
        </motion.div>

        <motion.div
          className="h-0.5 w-12 origin-center rounded-full"
          style={{ background: '#9DB0E6' }}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ delay: 0.85, duration: 0.5, ease: 'easeOut' }}
        />
      </div>
    </motion.div>
  );
}
